<script setup>
  import { ref, computed, toRef, watch, onMounted, onBeforeUnmount } from 'vue';
  import {
    Play as PlayIcon,
    Pause as PauseIcon,
    ChevronUp as ChevronUpIcon,
    ChevronDown as ChevronDownIcon,
    AudioLines as AudioLinesIcon,
    Volume2 as Volume2Icon,
    Volume1 as Volume1Icon,
    VolumeX as VolumeXIcon,
  } from 'lucide-vue-next';
  import { Button } from '@/components/ui/button/index.ts';
  import { Spinner } from '@/components/ui/spinner/index.ts';
  import { useHlsPlaylistPlayback } from '@utils/composables/useHlsPlaylistPlayback.ts';

  const props = defineProps({
    items: { type: Array, default: () => [] },
    playlistUrl: { type: String, default: null },
  });

  const emit = defineEmits(['media-change', 'refresh-expired']);

  const videoEl = ref(null);
  const playbackRate = ref(1);
  const collapsed = ref(false);

  const SPEEDS = [1, 1.5, 2];

  const VOLUME_STORAGE_KEY = 'interview-media-volume';
  const rawStoredVolume = localStorage.getItem(VOLUME_STORAGE_KEY);
  const storedVolume = rawStoredVolume === null ? NaN : Number(rawStoredVolume);
  const volume = ref(Number.isFinite(storedVolume) && storedVolume >= 0 && storedVolume <= 1 ? storedVolume : 1);
  const muted = ref(false);

  const volumeIcon = computed(() => {
    if (muted.value || volume.value === 0) return VolumeXIcon;
    return volume.value < 0.5 ? Volume1Icon : Volume2Icon;
  });

  const toggleMute = () => {
    muted.value = !muted.value;
  };

  const onVolumeInput = (value) => {
    volume.value = value;
    muted.value = value === 0;
  };

  const audioOnly = computed(() => props.items.length > 0 && props.items.every((i) => i.type === 'audio'));
  const hasMedia = computed(() => Boolean(props.playlistUrl) || props.items.length > 0);

  const {
    playing, timelineSeconds, totalSeconds, loading,
    onTimeUpdate, onLoadedMetadata, onEnded, onError, onCanPlay, onWaiting,
    togglePlay, seekTo, jumpToItemId, setRate, applyVolume, mount, unmount,
  } = useHlsPlaylistPlayback({
    videoEl,
    items: toRef(props, 'items'),
    src: toRef(props, 'playlistUrl'),
    transport: { playbackRate, volume, muted },
    emit,
  });

  const cycleSpeed = () => {
    const idx = SPEEDS.indexOf(playbackRate.value);
    playbackRate.value = SPEEDS[(idx + 1) % SPEEDS.length];
    setRate(playbackRate.value);
  };

  const formatTime = (seconds) => {
    if (!seconds || !Number.isFinite(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  watch([volume, muted], () => {
    applyVolume();
    localStorage.setItem(VOLUME_STORAGE_KEY, String(volume.value));
  });

  onMounted(mount);
  onBeforeUnmount(unmount);

  defineExpose({ jumpToItemId });
</script>

<template>
  <div class="rounded-lg bg-background">
    <div
      v-if="audioOnly"
      class="w-full h-[120px] flex flex-col items-center justify-center gap-2 rounded-t-lg bg-muted">
      <AudioLinesIcon class="h-8 w-8 text-muted-foreground" />
      <span class="text-xs font-medium text-muted-foreground">Audio responses</span>
    </div>

    <div class="relative">
      <video
        ref="videoEl"
        :class="[
          audioOnly
            ? 'sr-only'
            : ['w-full rounded-t-lg transition-[max-height] duration-[300ms] ease-linear', collapsed ? 'max-h-[120px]' : 'max-h-[300px]'],
        ]"
        playsinline
        @timeupdate="onTimeUpdate"
        @loadedmetadata="onLoadedMetadata"
        @canplay="onCanPlay"
        @waiting="onWaiting"
        @ended="onEnded"
        @error="onError" />
      <div
        v-if="loading && !audioOnly"
        class="absolute inset-0 flex items-center justify-center bg-background/40">
        <Spinner class="size-8 text-primary" />
      </div>
    </div>

    <div class="flex items-center gap-2 px-3 py-2 rounded-b-lg bg-muted">
      <Button
        variant="ghost"
        size="icon"
        class="h-8 w-8 shrink-0"
        :disabled="!hasMedia"
        @click="togglePlay">
        <component
          :is="playing ? PauseIcon : PlayIcon"
          class="h-4 w-4" />
      </Button>
      <div class="flex-1 min-w-0 flex items-center gap-2">
        <span class="text-xs text-muted-foreground font-mono w-10 text-right shrink-0">{{ formatTime(timelineSeconds) }}</span>
        <input
          type="range"
          :value="timelineSeconds"
          :min="0"
          :max="totalSeconds || 1"
          :step="0.1"
          class="flex-1 min-w-0 h-1 accent-primary cursor-pointer"
          @input="seekTo($event.target.valueAsNumber)" />
        <span class="text-xs text-muted-foreground font-mono w-10 shrink-0">{{ formatTime(totalSeconds) }}</span>
      </div>
      <div class="relative shrink-0 flex items-center group/vol">
        <Button
          variant="ghost"
          size="icon"
          class="h-8 w-8"
          :title="muted ? 'Unmute' : 'Mute'"
          @click="toggleMute">
          <component
            :is="volumeIcon"
            class="h-4 w-4" />
        </Button>
        <div class="absolute bottom-full left-1/2 -translate-x-1/2 pb-2 hidden group-hover/vol:block z-10">
          <div class="flex flex-col items-center rounded-lg border-2 border-border-strong bg-white p-3 shadow-2xl">
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              :value="muted ? 0 : volume"
              class="h-16 w-4 cursor-pointer accent-primary [writing-mode:vertical-lr] [direction:rtl]"
              @input="onVolumeInput($event.target.valueAsNumber)" />
          </div>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        class="h-7 px-2 text-xs font-mono shrink-0"
        @click="cycleSpeed">
        {{ playbackRate }}x
      </Button>
      <Button
        v-if="!audioOnly"
        variant="ghost"
        size="icon"
        class="h-8 w-8 shrink-0"
        :title="collapsed ? 'Expand' : 'Collapse'"
        @click="collapsed = !collapsed">
        <component
          :is="collapsed ? ChevronDownIcon : ChevronUpIcon"
          class="h-4 w-4" />
      </Button>
    </div>
  </div>
</template>
